import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { statusPillClasses, statusDotClasses } from "@/lib/format";
import { Check, Lock, AlertTriangle } from "lucide-react";

export default function StatusInline({ list, task, canEdit, onChange, asPill = true }) {
  const statuses = (list?.statuses || []).slice().sort((a, b) => a.order - b.order);
  const current = statuses.find((s) => s.id === task.status_id);
  const rules = list?.workflow_rules || [];

  const hints = statuses.map((s) => {
    const matching = rules.filter((r) => r.to_status === s.id && (r.from_status == null || r.from_status === task.status_id));
    const needsTime = matching.some((r) => r.require_time_logged) && (task.total_time_seconds || 0) <= 0;
    const allowedFrom = matching.find((r) => r.allowed_from_statuses);
    const blocked = allowedFrom && !allowedFrom.allowed_from_statuses.includes(task.status_id);
    return { status: s, needsTime, blocked };
  });

  const trigger = asPill ? (
    <button
      disabled={!canEdit}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${current ? statusPillClasses(current.color) : "bg-neutral-100 text-neutral-500"} ${canEdit ? "hover:ring-2 hover:ring-offset-1 hover:ring-neutral-300" : ""}`}
      data-testid={`inline-status-${task.id}`}
    >
      {current?.name || "—"}
    </button>
  ) : (
    <button
      disabled={!canEdit}
      onClick={(e) => e.stopPropagation()}
      className="flex items-center gap-1 text-xs"
      data-testid={`inline-status-${task.id}`}
    >
      <span className={`w-2 h-2 rounded-full ${statusDotClasses(current?.color || "neutral")}`} />
      <span>{current?.name || "—"}</span>
    </button>
  );

  if (!canEdit) return trigger;

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start" onClick={(e) => e.stopPropagation()}>
        <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 px-2 py-1.5">Set status</div>
        <div className="space-y-0.5">
          {hints.map(({ status, needsTime, blocked }) => {
            const selected = status.id === task.status_id;
            return (
              <button
                key={status.id}
                onClick={(e) => { e.stopPropagation(); onChange(status.id); }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-neutral-100 text-left transition-colors"
                data-testid={`inline-status-option-${status.id}`}
              >
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${statusPillClasses(status.color)}`}>
                  {status.name}
                </span>
                {needsTime && <Lock className="w-3 h-3 text-yellow-700 ml-1" />}
                {blocked && <AlertTriangle className="w-3 h-3 text-red-600 ml-1" />}
                {selected && <Check className="w-3 h-3 ml-auto text-neutral-700" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
